/**
 * Areas and projects nothing has happened in (SPEC.md §8.5).
 *
 * §8.5 calls the area level "the one worth having": noticing that *nothing at all* has
 * happened in `cinema` for nine days requires spotting an absence, which no conventional
 * task manager does. Projects sit alongside it for the case §8.5 singles out — "no child
 * activity, even with open tasks", a project that looks alive because it is full of things
 * nobody is doing.
 *
 * Slipping *tasks* are not here. They are a group in the task list, where they can be acted
 * on without being found again first.
 *
 * Each entry expands into its open tasks, with the ordinary controls. That is the whole
 * difference between this and the list it replaced: knowing `cinema` has gone quiet is only
 * useful if you can do something about it in the same breath.
 */

import { useState } from "react";
import { type Area, type Project, type SlippingEntry } from "../api";
import { type TaskActions } from "./TaskList";
import { TaskDrawer } from "./TaskDrawer";
import { EyeToggle } from "./EyeToggle";

export function GoneQuiet({
  areas,
  projects,
  today,
  allAreas,
  allProjects,
  actions,
  stillQuiet = () => true,
  refreshKey,
  onChanged,
}: {
  areas: SlippingEntry[];
  projects: SlippingEntry[];
  today: string;
  allAreas: Area[];
  allProjects: Project[];
  actions: TaskActions;
  /**
   * Whether an entry is *currently* quiet. Entries that have since been picked up stay
   * listed until the page reloads — acting on one must not delete the row you acted from.
   */
  stillQuiet?: (id: string) => boolean;
  /** Bumped when the briefing reloads, so open drawers refetch rather than going stale. */
  refreshKey?: number;
  /** Called after a drawer changes anything, so the card around this can refresh too. */
  onChanged?: () => void | Promise<void>;
}) {
  const listed = areas.length + projects.length;
  const total =
    areas.filter((e) => stillQuiet(e.id)).length +
    projects.filter((e) => stillQuiet(e.id)).length;

  if (listed === 0) {
    return (
      <section className="panel">
        <header>
          <h2>Gone quiet</h2>
          <span className="muted small">Whole areas and projects with no activity</span>
        </header>
        <p className="muted small">
          Nothing has gone quiet past its threshold. Every area has had something happen in it
          recently.
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <header>
        <h2>Gone quiet{total > 0 && ` · ${total}`}</h2>
        <span className="muted small">
          {total === 0 ? "All picked back up" : "Nothing at all has happened here"}
        </span>
      </header>

      <ul className="slip-list">
        {areas.map((entry) => (
          <QuietRow
            key={entry.id}
            entry={entry}
            kind="area"
            quiet={stillQuiet(entry.id)}
            refreshKey={refreshKey}
            onChanged={onChanged}
            today={today}
            allAreas={allAreas}
            allProjects={allProjects}
            actions={actions}
            severe
          />
        ))}
        {projects.map((entry) => (
          <QuietRow
            key={entry.id}
            entry={entry}
            kind="project"
            quiet={stillQuiet(entry.id)}
            refreshKey={refreshKey}
            onChanged={onChanged}
            today={today}
            allAreas={allAreas}
            allProjects={allProjects}
            actions={actions}
          />
        ))}
      </ul>
    </section>
  );
}

function QuietRow({
  entry,
  kind,
  today,
  allAreas,
  allProjects,
  actions,
  quiet,
  refreshKey,
  onChanged,
  severe = false,
}: {
  entry: SlippingEntry;
  kind: "area" | "project";
  quiet: boolean;
  refreshKey?: number;
  onChanged?: () => void | Promise<void>;
  today: string;
  allAreas: Area[];
  allProjects: Project[];
  actions: TaskActions;
  severe?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <li className={`slip${severe && quiet ? " bad" : ""}${quiet ? "" : " revived"}`}>
        <span>
          {kind === "area" ? (
            <>
              Nothing has happened in <strong>{entry.name}</strong>
            </>
          ) : (
            <>
              <strong>{entry.name}</strong>
              <span className="muted small"> in {entry.area_name}</span>
            </>
          )}
          {entry.open_tasks !== undefined && entry.open_tasks > 0 && (
            <span className="muted small">
              {" "}
              - {entry.open_tasks} open {entry.open_tasks === 1 ? "task" : "tasks"}
            </span>
          )}
        </span>
        <span className="row">
          {quiet ? (
            <span className="days" title={`Threshold is ${entry.stale_after_days} days`}>
              {entry.days_since_touch}d
            </span>
          ) : (
            <span className="days ok" title="Something happened here just now">
              picked up
            </span>
          )}
          <EyeToggle
            open={open}
            onToggle={() => setOpen((v) => !v)}
            label={`tasks in ${entry.name}`}
          />
        </span>
      </li>

      {open && (
        <li className="quiet-tasks">
          <TaskDrawer
            kind={kind}
            containerId={entry.id}
            containerName={entry.name}
            today={today}
            areas={allAreas}
            projects={allProjects}
            actions={actions}
            refreshKey={refreshKey}
            onChanged={onChanged}
          />
        </li>
      )}
    </>
  );
}
